import React, { useState, useEffect } from "react";
import NoNotification from "../../img/no-results.png";
import { NavLink } from "react-router-dom";
import "./recentactivity.css";
import "../ProgressBySector/sectorprogress.css";

// Inline styles for the component
const styles = {
  searchContainer: {
    width: "250px",
    position: "relative",
  },
  searchInput: {
    borderRadius: "20px",
    paddingLeft: "15px",
    paddingRight: "30px",
    border: "1px solid #ddd",
    height: "35px",
    fontSize: "14px",
  },
  activityTableHeader: {
    padding: "10px 15px",
    backgroundColor: "#f8f9fa",
    borderRadius: "5px",
    fontWeight: 600,
    color: "#495057",
    fontSize: "14px",
    borderBottom: "2px solid #e9ecef",
  },
  activityRow: {
    padding: "10px 15px",
    borderBottom: "1px solid #e9ecef",
    transition: "background-color 0.2s",
    cursor: "pointer",
  },
  activityRowHover: {
    backgroundColor: "#f8f9fa",
  },
  dateTime: {
    fontSize: "13px",
    color: "#6c757d",
  },
  date: {
    fontWeight: 500,
    marginBottom: "2px",
  },
  time: {
    fontSize: "12px",
  },
  circleIcon: {
    width: "35px",
    height: "35px",
    borderRadius: "50%",
    backgroundColor: "#3F88A5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: "10px",
  },
  nameIcon: {
    color: "white",
    fontWeight: 600,
    fontSize: "16px",
  },
  name: {
    fontWeight: 500,
    fontSize: "14px",
    color: "#343a40",
  },
  messageLink: {
    color: "#495057",
    textDecoration: "none",
  },
  messageLinkHover: {
    color: "#3F88A5",
    textDecoration: "underline",
  },
  noResults: {
    textAlign: "center",
    width: "100%",
    padding: "3rem 0",
  },
  noResultsImage: {
    width: "25%",
    maxWidth: "200px",
  },
  loadingSpinner: {
    textAlign: "center",
    padding: "0.75rem 0",
  },
  scrollContainer: {
    height: "calc(100% - 90px)",
    overflowY: "auto",
  },
  // Adjusted column widths without event column
  timeCol: { width: "15%" },
  userCol: { width: "25%" },
  messageCol: { width: "45%" },
  statusCol: { width: "15%" },
  // Status badges
  statusBadge: {
    padding: "4px 10px",
    borderRadius: "4px",
    textAlign: "center",
    display: "inline-block",
    fontWeight: "500",
    fontSize: "12px",
  },
  successStatus: {
    backgroundColor: "#d4edda",
    color: "#155724",
  },
  failedStatus: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
  },
  pendingStatus: {
    backgroundColor: "#fff3cd",
    color: "#856404",
  }
};

const UsersActivity = ({ usersActivity, heading }) => {
  // State for search and filtering
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [loadedActivities, setLoadedActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);

  const itemsPerLoad = 10;

  // Format date as DD/MM/YYYY
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format time in AM/PM format
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  // Filter activities based on search term
  useEffect(() => {
    if (!usersActivity) return;
    
    const filtered = usersActivity.filter((activity) => {
      return (
        activity?.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity?.message?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        activity?.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        formatDate(activity?.createdAt).includes(searchTerm) ||
        formatTime(activity?.createdAt).toLowerCase().includes(searchTerm.toLowerCase()) ||
        (activity?.metadata && JSON.stringify(activity?.metadata).toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });
    
    setFilteredActivities(filtered);
    setLoadedActivities(filtered.slice(0, itemsPerLoad));
  }, [searchTerm, usersActivity]);

  // Load more activities on scroll
  const loadMoreActivities = () => {
    if (loading) return;
    setLoading(true);

    const currentLength = loadedActivities.length;
    const nextActivities = filteredActivities.slice(
      currentLength,
      currentLength + itemsPerLoad
    );
    
    setLoadedActivities((prev) => [...prev, ...nextActivities]);
    setLoading(false);
  };

  // Handle scroll to load more data
  const handleScroll = (e) => {
    const bottom =
      Math.abs(
        e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight
      ) < 5;
    if (bottom) {
      loadMoreActivities();
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Get status badge style based on status value
  const getStatusStyle = (status) => {
    const statusText = status ? status.toLowerCase() : "";
    
    if (statusText === "success" || statusText === "completed") {
      return { ...styles.statusBadge, ...styles.successStatus };
    } else if (statusText === "failed" || statusText === "error") {
      return { ...styles.statusBadge, ...styles.failedStatus };
    } else if (statusText === "pending" || statusText === "in progress") {
      return { ...styles.statusBadge, ...styles.pendingStatus };
    } else {
      // Default style for unknown status
      return { ...styles.statusBadge, backgroundColor: "#e9ecef", color: "#495057" };
    }
  };

  const getStatusClass = (status) => {
    const statusText = status ? status.toLowerCase() : "";
    if (statusText === "success" || statusText === "completed") {
      return "status-success";
    } else if (statusText === "failed" || statusText === "error") {
      return "status-failed";
    } else if (statusText === "pending" || statusText === "in progress") {
      return "status-pending";
    }
    return "status-default";
  };

  return (
    <div className="recentclass">
      {/* Header with search */}
      <div className="activity-header-wrapper">
        <h3 className="activity-header-title">{heading}</h3>
        <div style={{ position: 'relative', width: '220px' }}>
          <i className="fas fa-search activity-search-icon" aria-hidden="true" />
          <input
            type="text"
            className="activity-search-input"
            placeholder="Search activities..."
            value={searchTerm}
            onChange={handleSearchChange}
            style={{ width: '100%', paddingLeft: '32px' }}
          />
        </div>
      </div>

      {/* Activity Table Header */}
      <div className="activity-table-header">
        <div style={{ width: "20%" }}>Date & Time</div>
        <div style={{ width: "25%" }}>User</div>
        <div style={{ width: "40%" }}>Event</div>
        <div style={{ width: "15%", textAlign: "center" }}>Status</div>
      </div>
      
      {/* Activity table body scroll */}
      <div
        className="activity_section"
        onScroll={handleScroll}
      >
        {loadedActivities.length > 0 ? (
          loadedActivities.map((notification) => (
            <div key={notification.id} className="activity-row">
              {/* Timestamp */}
              <div style={{ width: "20%" }}>
                <div style={{ fontSize: "11px", fontWeight: 600, color: "#1E293B" }}>
                  {formatDate(notification?.createdAt)}
                </div>
                <div style={{ fontSize: "11px", color: "#94A3B8", marginTop: "2px" }}>
                  {formatTime(notification?.createdAt)}
                </div>
              </div>
              
              {/* User Identity */}
              <div style={{ width: "25%", display: "flex", alignItems: "center", minWidth: 0, paddingRight: "8px" }}>
                <div className="circle-icon">
                  <div className="name_icon">
                    {notification?.userName?.charAt(0) || "U"}
                  </div>
                </div>
                <span 
                  className="name" 
                  style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} 
                  title={notification?.userName}
                >
                  {notification?.userName}
                </span>
              </div>
              
              {/* Message text link */}
              <div style={{ width: "40%", paddingRight: '8px', minWidth: 0, textOverflow: 'ellipsis', overflow: 'hidden' }}>
                <NavLink
                  className="activity-message-link"
                  onClick={() => {
                    if (notification?.questionId) {
                      localStorage.setItem(
                        "reportingQuestion",
                        JSON.stringify([notification?.questionId])
                      );
                    }
                  }}
                  to={{
                    pathname: notification?.questionId 
                      ? "/reporting-modules/all-module" 
                      : "#",
                    state: {
                      reportingQuestion: notification?.questionId 
                        ? [notification?.questionId] 
                        : [],
                    },
                  }}
                >
                  <span title={notification?.message}>
                    {notification?.message}
                  </span>
                </NavLink>
              </div>

              {/* Status Badge */}
              <div style={{ width: "15%", display: 'flex', justifyContent: 'center' }}>
                <span className={`status-badge ${getStatusClass(notification?.status)}`}>
                  {notification?.status || "Unknown"}
                </span>
              </div>
            </div>
          ))
        ) : (
          <div className="d-flex flex-column align-items-center justify-content-center w-100 py-5" style={{ height: '100%', minHeight: '300px' }}>
            <img
              src={NoNotification}
              alt="No Activities Found"
              style={{ width: '120px', opacity: 0.5 }}
            />
            <p className="mt-3 text-muted" style={{ fontSize: '12px' }}>No activities found</p>
          </div>
        )}
        {loading && (
          <div className="text-center py-2">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersActivity;